import React, { FunctionComponent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CardListItem, CardListItemProps } from 'record-it-components/cardList/CardListItem/CardListItem';
import { CardListItemTitleWithSubtitle } from 'record-it-components/cardList/CardListItemTitleWithSubtitle/CardListItemTitleWithSubtitle';
import { useEventCallback } from 'record-it-components/hooks/useEventCallback';
import { useOptionalLocationInfo } from 'record-it-components/hooks/useOptionalLocationInfo/useOptionalLocationInfo';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { classes } from 'record-it-components/utils/utils';
import { LocationInfoType } from 'record-it-utils/location/GeolocationManager/GeolocationManager';
import { LocationUtils } from 'record-it-utils/location/LocationUtils/LocationUtils';
import { NumberUtils } from 'record-it-utils/utils/NumberUtils/NumberUtils';
import { ConstructionSite } from '../../../classes/entities/ConstructionSite/constructionSiteTypes';
import styles from './ConstructionSiteToSelectListItem.module.css';
export type ConstructionSiteToSelectListItemProps = PropsWithoutChildren<CardListItemProps> & {
  constructionSite: ConstructionSite;
  onConstructionSiteSelected: (constructionSite: ConstructionSite) => void;
};
export const ConstructionSiteToSelectListItem: FunctionComponent<ConstructionSiteToSelectListItemProps> = React.memo(props => {
  const {
    constructionSite,
    onConstructionSiteSelected,
    onClick,
    className,
    ...remainingProps
  } = prepareProps({
    props,
    ignoreChildren: true
  });
  const {
    t
  } = useTranslation();
  const handleClick: CardListItemProps['onClick'] = useEventCallback(event => {
    onClick?.(event);
    onConstructionSiteSelected(constructionSite);
  });
  const locationInfo = useOptionalLocationInfo();
  const formattedDistance = useMemo(() => {
    if (!constructionSite.location || locationInfo.type !== LocationInfoType.Available) {
      return;
    }
    return NumberUtils.formatCoarseDistance(LocationUtils.distanceBetweenCoordinates(locationInfo, constructionSite.location));
  }, [locationInfo, constructionSite]);
  return <CardListItem {...remainingProps} className={classes(className, styles.card)} onClick={handleClick}>
        <CardListItemTitleWithSubtitle>
          {() => ({
        title: <span data-testid="ConstructionSiteToSelectListItem.Name">
                {constructionSite.name}
              </span>,
        subtitle: formattedDistance ? <span data-testid="ConstructionSiteToSelectListItem.Distance">
                {t('components.constructionSite.ConstructionSiteToSelectListItem.distance')}
                {': '}
                {formattedDistance}
              </span> : null
      })}
        </CardListItemTitleWithSubtitle>
      </CardListItem>;
});